import { useState, useEffect, useCallback, useRef } from 'react'
import { clientSessionViewStore } from '../../../services/clientSessionViewStore.js'
import { TranscriptItem, ResearchResult } from 'types'
import { logger } from '../../lib/logger-client.js'
import { shouldResetPersonaForEmail } from '../../lib/persona-session-reset.js'
import {
  mintFreshClientSessionForNewVisitor,
  shouldMintFreshSessionForIntake,
  visitorEmailsMatch,
} from '../../lib/visitor-session-isolation.js'
import {
  getLeadResearchGreetingEducational,
  getLeadResearchGreetingRich,
  isHedgeWelcome,
  isFirstConsultingWelcome,
  isLabeledGuessWelcome,
  isNoSaleEducationLead,
  isResearchAckWelcome,
  isUngroundedResearchClaim,
  isUpgradeableWelcome,
  openerCompanyName,
  shouldUpgradeCannedGuess,
} from '../../core/prompts/greetings.js'
import { GREETING_PRESENCE_MS, PREFLIGHT_UI_TIMEOUT_MS } from '../../core/contracts.js'
import { readGroundedPack } from '../../core/intelligence/grounded-pack.js'
import { deriveCompanySearchLabel, normalizeResearchDomainHost, splitCompanyUrlOrName } from '../../lib/company-search-label.js'
import { isGenericOrFreeDomain } from '../../lib/company-binding.js'
import { isUiLocaleExplicit } from '../../i18n/resolve-ui-locale.js'
import { coerceUiLocale } from '../../core/contracts.js'
import { openerLocaleForPlate } from '../../lib/reply-language.js'
import { citationSupportsQuestion, spokenSourceCount } from '../../lib/spoken-sources.js'
import { GENERIC_DOMAINS } from '../../config/constants.js'
import { ADMIN_CONFIG } from '../../config/constants.js'
import type { VerificationResult } from '../../core/intelligence/verification.js'
import { useLocaleContext } from '../../context/LocaleContext.js'
import {
  buildVerifiedSessionResearch,
  enrichResearchForPublicBrief,
  isHighConfidenceVerification,
  mergeIdentityVerification,
  mergeResearchIntoIntelligenceContext,
  shouldRevealPublicBrief,
} from '../../lib/identity-briefing-merge.js'
import { acceptEmbeddedSessionProjection } from '../../lib/accept-session-projection-response.js'
import type { CanonicalSessionPermissionsV1 } from '../../core/session/session-projection.js'
import {
  ensureSessionCapability,
  SESSION_HEADER,
  waitForClientSessionLookup,
} from '../../core/session/session-identity.js'

function derivePreferredDomains(email: string, companyUrl?: string): string[] {
  const domains = new Set<string>()
  const emailDomain = email.split('@')[1]?.toLowerCase().trim()
  if (emailDomain) {
    domains.add(normalizeResearchDomainHost(emailDomain) || emailDomain)
  }

  if (companyUrl) {
    const hostname = normalizeResearchDomainHost(companyUrl)
    if (hostname) domains.add(hostname)
  }

  return Array.from(domains)
}

function buildPlateOnlyResearchResult(
  email: string,
  name: string | undefined,
  plate: string
): ResearchResult {
  const personName = name?.trim() || email.split('@')[0] || 'Unknown'
  return {
    company: {
      name: plate,
      domain: '',
      summary: '',
    },
    person: {
      fullName: personName,
      company: plate,
    },
    role: 'Unknown',
    confidence: 0,
    citations: [],
  }
}

function buildFallbackResearchResult(
  email: string,
  name?: string,
  companyUrl?: string
): ResearchResult {
  const domain = email.split('@')[1] || 'unknown.com'
  const genericDomains = new Set(['gmail.com','outlook.com','hotmail.com','yahoo.com','icloud.com','proton.me','protonmail.com','aol.com','live.com','mail.com','example.com','test.com'])
  const isGeneric = genericDomains.has(domain.toLowerCase())
  const companyName = isGeneric ? 'Unknown Company' : (domain.split('.')[0] || 'Unknown Company')
  const personName = name || email.split('@')[0] || 'Unknown'
  return {
    company: {
      name: companyName,
      domain,
      website: companyUrl || `https://${domain}`,
      summary: 'Company information unavailable',
    },
    person: {
      fullName: personName,
      company: companyName,
    },
    role: 'Business Professional',
    confidence: 0,
    citations: [],
  }
}

export interface UserProfile {
  name: string
  email: string
  company?: string
  role?: string
  source?: 'intake' | 'research' | 'restore'
}

interface UseLeadResearchProps {
  sessionId: string
  setTranscript: React.Dispatch<React.SetStateAction<TranscriptItem[]>>
  setIsWebcamActive: (active: boolean) => void
  onVoiceConnect: () => void
  canAutoStartVoice: () => boolean
  setView: (view: 'landing' | 'chat' | 'admin') => void
  setChatVisible: (visible: boolean) => void
  showToast: (message: string, type: 'success' | 'error' | 'info') => void
  setVisualState: React.Dispatch<React.SetStateAction<any>>
}

export function useLeadResearch({
  sessionId,
  setTranscript,
  setIsWebcamActive,
  onVoiceConnect,
  canAutoStartVoice,
  setView,
  setChatVisible,
  showToast,
  setVisualState,
  researchResultRef, // External Ref
  intelligenceContextRef, // External Ref
}: UseLeadResearchProps & {
  researchResultRef: React.MutableRefObject<ResearchResult | null>
  intelligenceContextRef: React.MutableRefObject<any>
}) {
  const { t, locale } = useLocaleContext()
  const lastGreetingPayloadRef = useRef<{
    email: string
    name?: string
    companyUrl?: string
    research?: ResearchResult
    intelligenceContext?: any
    trustedIdentity?: boolean
  } | null>(null)
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null)
  const [locationData, setLocationData] = useState<{
    latitude: number
    longitude: number
    city?: string
    country?: string
  } | null>(null)
  const [isResearching, setIsResearching] = useState(false)
  const [researchStatus, setResearchStatus] = useState<string>('')
  const [sessionPermissions, setUiSessionPermissions] =
    useState<CanonicalSessionPermissionsV1 | null>(null)
  const [preflightFailed, setPreflightFailed] = useState(false)
  const [preflightTimedOut, setPreflightTimedOut] = useState(false)
  const preflightTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const presenceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const initialGreetingSentRef = useRef(false)
  // The opener is stamped once. A later research or locale replace keeps this instant.
  const openerTimestampRef = useRef<Date | null>(null)
  const initSessionInflightRef = useRef<Map<string, Promise<unknown>>>(new Map())
  const pendingVoiceAutostartRef = useRef(false)

  const runInitSessionOnce = useCallback(
    async <T>(sessionId: string, request: () => Promise<T>): Promise<T> => {
      const inflight = initSessionInflightRef.current
      const existing = inflight.get(sessionId)
      if (existing) return existing as Promise<T>

      const promise = request().finally(() => {
        if (inflight.get(sessionId) === promise) {
          inflight.delete(sessionId)
        }
      })
      inflight.set(sessionId, promise)
      return promise
    },
    []
  )

  const waitForChatVoiceAutostart = useCallback(async () => {
    const timeoutAt = Date.now() + 5000

    while (Date.now() < timeoutAt) {
      if (canAutoStartVoice()) {
        return true
      }
      await new Promise(resolve => setTimeout(resolve, 50))
    }

    return canAutoStartVoice()
  }, [canAutoStartVoice])

  const postJson = useCallback(
    async <T>(path: string, payload: unknown, options?: RequestInit): Promise<T> => {
      const payloadSessionId =
        payload && typeof payload === 'object' && !Array.isArray(payload) &&
        typeof (payload as Record<string, unknown>).sessionId === 'string'
          ? String((payload as Record<string, unknown>).sessionId).trim()
          : ''
      if (payloadSessionId) await ensureSessionCapability(payloadSessionId)
      const headers = new Headers(options?.headers)
      headers.set('Content-Type', 'application/json')
      if (payloadSessionId) headers.set(SESSION_HEADER, payloadSessionId)
      const retryConflicts = path.includes('/intelligence/init-session')
      const maxAttempts = retryConflicts ? 3 : 1
      let lastText = ''
      for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
        const res = await fetch(path, {
          ...options,
          method: 'POST',
          credentials: 'same-origin',
          headers,
          body: JSON.stringify(payload),
        })
        lastText = await res.text()
        try {
          const parsed: unknown = JSON.parse(lastText)
          acceptEmbeddedSessionProjection(parsed, path)
          const conflict =
            !res.ok &&
            (res.status === 404 || res.status === 409 || res.status === 500) &&
            retryConflicts
          if (conflict && attempt < maxAttempts - 1) {
            await new Promise(resolve => setTimeout(resolve, 80 * (attempt + 1)))
            continue
          }
          return parsed as T
        } catch {
          if (retryConflicts && attempt < maxAttempts - 1) {
            await new Promise(resolve => setTimeout(resolve, 80 * (attempt + 1)))
            continue
          }
          throw new Error(lastText || `Request failed: ${res.status}`)
        }
      }
      throw new Error(lastText || 'Request failed')
    },
    []
  )

  // Restore profile on mount
  useEffect(() => {
    const savedProfile = sessionStorage.getItem('fbc_user_profile')
    if (savedProfile) {
      const parsedProfile = JSON.parse(savedProfile) as UserProfile | null
      setUserProfile(parsedProfile)
    }

    const syncProjection = (
      state: ReturnType<typeof clientSessionViewStore.getSnapshot>
    ) => {
      if (state.sessionPermissions) {
        const nextPermissions = {
          voice: state.sessionPermissions.voice,
          webcam: state.sessionPermissions.webcam,
          location: state.sessionPermissions.location,
          research: state.sessionPermissions.research,
          screen: state.sessionPermissions.screen,
          files: state.sessionPermissions.files,
        }
        setUiSessionPermissions(nextPermissions)
      }
    }

    syncProjection(clientSessionViewStore.getSnapshot())
    const unsubscribe = clientSessionViewStore.subscribe(syncProjection)
    return () => {
      unsubscribe()
    }
  }, [])

  const applyServerResearchToContext = useCallback(
    (
      email: string,
      name: string | undefined,
      _companyUrl: string | undefined,
      payload: {
        research?: ResearchResult
        intelligenceContext?: any
        trustedIdentity?: boolean
      },
      options?: { autoRevealBriefing?: boolean }
    ) => {
      const research = payload.research
      const snapshotVerification = (
        clientSessionViewStore.getSnapshot().intelligenceContext as
          | { verification?: VerificationResult }
          | undefined
      )?.verification
      const currentVerification = mergeIdentityVerification(
        payload.intelligenceContext?.verification as VerificationResult | undefined,
        snapshotVerification
      )
      const hasTrustedIdentity = Boolean(
        payload.trustedIdentity === true ||
          payload.intelligenceContext?.trustedIdentity === true ||
          payload.intelligenceContext?.research?.trustedIdentity === true ||
          currentVerification?.verified === true
      )
      const hasVerifiedSession = isHighConfidenceVerification(currentVerification)
      const sessionResearch =
        research && (hasTrustedIdentity || hasVerifiedSession)
          ? buildVerifiedSessionResearch(research, currentVerification)
          : null
      let mergedIntelligenceContext = research
        ? mergeResearchIntoIntelligenceContext(
            {
              ...(payload.intelligenceContext || {
                email,
                name: name || email.split('@')[0] || 'Unknown',
              }),
            },
            research,
            currentVerification
          )
        : {
            ...(payload.intelligenceContext || {
              email,
              name: name || email.split('@')[0] || 'Unknown',
            }),
            ...(currentVerification ? { verification: currentVerification } : {}),
          }
      if (research && !isHighConfidenceVerification(currentVerification)) {
        mergedIntelligenceContext = {
          ...mergedIntelligenceContext,
          research: {
            ...(typeof mergedIntelligenceContext.research === 'object' &&
            mergedIntelligenceContext.research !== null
              ? (mergedIntelligenceContext.research as Record<string, unknown>)
              : {}),
            citations: research.citations ?? [],
          },
        }
      }
      // Current-session intake beats email-derived research when they conflict.
      const intakeCompany = _companyUrl?.trim()
      const intakeName = name?.trim()
      if (intakeCompany || intakeName) {
        const intakeOverrides: Record<string, unknown> = { ...mergedIntelligenceContext }
        if (intakeName) intakeOverrides.name = intakeName
        const existingPerson =
          typeof mergedIntelligenceContext.person === 'object' &&
          mergedIntelligenceContext.person !== null
            ? (mergedIntelligenceContext.person as Record<string, unknown>)
            : {}
        if (intakeName || intakeCompany) {
          intakeOverrides.person = {
            ...existingPerson,
            ...(intakeName ? { fullName: intakeName } : {}),
            ...(intakeCompany ? { company: intakeCompany } : {}),
          }
        }
        if (intakeCompany) {
          intakeOverrides.company = {
            ...(typeof mergedIntelligenceContext.company === 'object' &&
            mergedIntelligenceContext.company !== null
              ? (mergedIntelligenceContext.company as Record<string, unknown>)
              : {}),
            name: intakeCompany,
          }
        }
        mergedIntelligenceContext = intakeOverrides
      }

      const committedView = clientSessionViewStore.getSnapshot()
      researchResultRef.current =
        sessionResearch || research || committedView.researchContext || null
      intelligenceContextRef.current =
        Object.keys(mergedIntelligenceContext).length > 0
          ? mergedIntelligenceContext
          : committedView.intelligenceContext || {}

      if (research && options?.autoRevealBriefing === true) {
        const hasRenderablePublicBrief = shouldRevealPublicBrief({
          ...(payload.trustedIdentity !== undefined
            ? { trustedIdentity: payload.trustedIdentity }
            : {}),
          ...(research ? { research } : {}),
          ...(payload.intelligenceContext ? { intelligenceContext: payload.intelligenceContext } : {}),
          ...(currentVerification ? { verification: currentVerification } : {}),
        })

        if (!hasRenderablePublicBrief) {
          logger.info('[F.B/c Briefing] Suppressed briefing card', {
            reason: 'not_source_backed_or_low_confidence',
            person: research.person?.fullName,
            company: research.company?.name,
            confidence: research.confidence,
            verificationConfidence: currentVerification?.confidence,
            trustedIdentity: payload.trustedIdentity,
          })
          return
        }

        const researchForBriefing =
          sessionResearch || enrichResearchForPublicBrief(research, currentVerification)
        const briefingTrustedIdentity = Boolean(
          payload.trustedIdentity === true ||
            payload.intelligenceContext?.research?.trustedIdentity === true ||
            hasVerifiedSession
        )

        const briefingId = `briefing-${Date.now()}`
        const intelligenceBriefing: TranscriptItem = {
          id: briefingId,
          role: 'model',
          text: `[Intelligence Briefing] Research complete for ${researchForBriefing.person?.fullName || name}.`,
          timestamp: new Date(),
          isFinal: true,
          attachment: {
            type: 'intelligence_briefing',
              data: JSON.stringify({
                verification: currentVerification || {},
                trustedIdentity: briefingTrustedIdentity,
                research: researchForBriefing,
              }),
            name: 'Intelligence Briefing',
          },
          groundingMetadata:
            researchForBriefing.groundingMetadata ||
            (researchForBriefing.citations?.length
              ? {
                  groundingChunks: researchForBriefing.citations.map((c: any) => ({
                    web: { uri: c.uri || c.url, title: c.title || 'Source' },
                  })),
                  webSearchQueries: [],
                }
              : undefined),
        }

        logger.debug('[F.B/c Briefing] Prepared public briefing:', {
          person: researchForBriefing.person?.fullName,
          company: researchForBriefing.company?.name,
          sourceCount: researchForBriefing.citations?.length || 0,
          isVerified: Boolean(currentVerification?.verified),
        })

        const currentTranscript = clientSessionViewStore.getSnapshot().transcript
        const lastMessage = currentTranscript[currentTranscript.length - 1]
        if (!(lastMessage?.attachment?.type === 'intelligence_briefing')) {
          clientSessionViewStore.upsertOptimisticTranscriptItem(intelligenceBriefing)
          setTranscript(prev => [...prev, intelligenceBriefing])
        }
      }
    },
    [intelligenceContextRef, researchResultRef, setTranscript]
  )

  const maybeInjectPersonalizedGreeting = useCallback(
    (
      payload: {
        email: string
        name?: string
        companyUrl?: string
        research?: ResearchResult
        intelligenceContext?: any
        trustedIdentity?: boolean
      },
      injectOptions?: { replaceForLocale?: boolean }
    ) => {
      lastGreetingPayloadRef.current = payload
      const resolvedName =
        payload.name ||
        payload.research?.person?.fullName ||
        payload.intelligenceContext?.name ||
        payload.email.split('@')[0] ||
        'there'

      const verification = mergeIdentityVerification(
        payload.intelligenceContext?.verification,
        undefined
      )
      const trustedIdentity = Boolean(
        payload.trustedIdentity === true ||
          payload.intelligenceContext?.trustedIdentity === true ||
          payload.intelligenceContext?.research?.trustedIdentity === true ||
          isHighConfidenceVerification(verification)
      )
      const sessionResearch =
        payload.research && verification
          ? buildVerifiedSessionResearch(payload.research, verification)
          : payload.research

      // Stated/research company+role+industry must reach the greeting even when
      // trustedIdentity is false — thin Proff research still upgrades to a guess.
      // Identity lock stays: we use stated/research names, not email-domain invention.
      const role = sessionResearch?.person?.role || sessionResearch?.role || null
      const industry = sessionResearch?.company?.industry || null
      const location =
        sessionResearch?.person?.location || sessionResearch?.company?.country || null
      const hasStrategicInsights = !!(
        sessionResearch?.strategic?.digital_maturity ||
        sessionResearch?.strategic?.pitch_strategy
      )
      // Identity-check URLs are not research. An empty citation list stays empty
      // even when verify-identity later copies its sources onto the brief.
      const spokenCitations = payload.research?.citations ?? []
      const citationUrls = spokenCitations
        .map((citation: { uri?: string; url?: string }) => citation.uri || citation.url || '')
        .filter((url: string) => url.trim().length > 0)
      const emailHost = payload.email.split('@')[1]?.toLowerCase().trim()
      const sourceCount = spokenSourceCount(citationUrls, emailHost)
      const citeable = spokenCitations.flatMap(
        (citation: { uri?: string; url?: string; title?: string }) => {
          const uri = citation.uri || citation.url || ''
          if (!uri) return []
          const supported = citationSupportsQuestion(
            uri,
            emailHost ? { ownDomain: emailHost } : {},
          )
          return supported ? [{ uri, title: citation.title || 'Source' }] : []
        },
      )
      const strategicHook = sessionResearch?.strategic?.personal_hook || null
      const groundedPack =
        readGroundedPack(sessionResearch) || readGroundedPack(payload.intelligenceContext)
      const siteSignal = [
        sessionResearch?.company?.summary,
        groundedPack?.company.summary,
        groundedPack?.pressurePoints?.[0]?.claim,
        ...(sessionResearch?.strategic?.pain_points || []),
      ]
        .filter((part): part is string => Boolean(part?.trim()))
        .join(' ')
      const hasBriefingCard = sourceCount > 0
      const emailDomain = payload.email.split('@')[1]?.toLowerCase().trim()
      const emailDomainCompany =
        emailDomain && !GENERIC_DOMAINS.has(emailDomain)
          ? deriveCompanySearchLabel({ emailDomain })
          : null
      const snapshotCompany = (
        clientSessionViewStore.getSnapshot().intelligenceContext as
          | { company?: { name?: string } }
          | undefined
      )?.company?.name
      const companyName = openerCompanyName({
        plate: payload.companyUrl,
        statedCompany:
          payload.intelligenceContext?.company?.name || snapshotCompany || null,
        researchCompany:
          sessionResearch?.company?.name || sessionResearch?.person?.company || null,
        verificationCompany: verification?.company || null,
        emailDomainCompany,
      })
      // A plate or research name is enough. A capitalized email domain is not
      // a company name, and citations are not required for the guess path.
      const canStateHypothesis =
        companyName !== 'your company' ||
        Boolean(role?.trim()) ||
        Boolean(industry?.trim()) ||
        sourceCount > 0 ||
        Boolean(strategicHook)
      const greetingCompany = canStateHypothesis ? companyName : 'your company'
      const canReplaceFallbackGreeting =
        initialGreetingSentRef.current &&
        (canStateHypothesis ||
          injectOptions?.replaceForLocale === true)
      const greetingLocale = clientSessionViewStore.getSnapshot().uiLocale ?? locale
      const translateGreeting = (key: string, options?: Record<string, unknown>) =>
        t(key, { ...options, lng: greetingLocale })
      const greeting = isNoSaleEducationLead(payload.email, resolvedName)
        ? getLeadResearchGreetingEducational(resolvedName, translateGreeting)
        : getLeadResearchGreetingRich(resolvedName, greetingCompany, {
            role,
            industry,
            location,
            hasStrategicInsights,
            sourceCount,
            trustedIdentity,
            hasBriefingCard,
            strategicHook,
            locale: greetingLocale,
            siteSignal,
          }, translateGreeting)

      const openerTimestamp = openerTimestampRef.current ?? new Date()
      const greetingItem: TranscriptItem = {
        id: `greeting-${openerTimestamp.getTime()}`,
        role: 'model',
        text: greeting,
        timestamp: openerTimestamp,
        isFinal: true,
        status: 'complete',
        ...(citeable.length > 0
          ? {
              groundingMetadata: {
                groundingChunks: citeable.map(citation => ({
                  web: { uri: citation.uri, title: citation.title },
                })),
                webSearchQueries: [],
              },
            }
          : {}),
      }

      let appended = false
      let greetingToPersist = greetingItem
      setTranscript(prev => {
        // Check specifically for TEXTUAL model replies that are NOT briefings
        const hasActualModelText = prev.some(
          item =>
            item.role === 'model' &&
            item.text?.trim() &&
            item.attachment?.type !== 'intelligence_briefing'
        )

        if (initialGreetingSentRef.current) {
          if (!canReplaceFallbackGreeting) return prev
          if (!isFirstConsultingWelcome(greeting) && !injectOptions?.replaceForLocale) return prev

          const fallbackGreetingIndex = prev.findIndex(
            item =>
              item.role === 'model' &&
              item.id.startsWith('greeting-') &&
              item.text?.trim() &&
              item.attachment?.type !== 'intelligence_briefing' &&
              (injectOptions?.replaceForLocale === true ||
                isHedgeWelcome(item.text) ||
                isUpgradeableWelcome(item.text) ||
                shouldUpgradeCannedGuess(item.text, greeting) ||
                (isResearchAckWelcome(greeting) &&
                  (!isResearchAckWelcome(item.text) ||
                    (greeting.includes("I've looked into") &&
                      !item.text?.includes("I've looked into")) ||
                    (greeting.includes('Jeg har sett') && !item.text?.includes('Jeg har sett')) ||
                    (greeting.includes('enough to start') &&
                      !item.text?.includes('enough to start')) ||
                    (greeting.includes('nok til å starte') &&
                      !item.text?.includes('nok til å starte')) ||
                    (greeting.includes('already started looking into') &&
                      !item.text?.includes('already started looking into')) ||
                    (greeting.includes('allerede begynt') && !item.text?.includes('allerede begynt')) ||
                    (/where do you want to steer|hvor vil du styre/i.test(greeting) &&
                      /i've looked into|jeg har sett p/i.test(item.text || '') &&
                      !/where do you want to steer|hvor vil du styre/i.test(item.text || '')))) ||
                (isLabeledGuessWelcome(item.text) && isResearchAckWelcome(greeting)) ||
                (Boolean(payload.research) &&
                  sourceCount === 0 &&
                  isUngroundedResearchClaim(item.text) &&
                  !isUngroundedResearchClaim(greeting)) ||
                (!item.groundingMetadata && !item.contextSources?.length))
          )
          if (fallbackGreetingIndex < 0) {
            // Projection/Live hydrate can wipe greeting-* — re-append if
            // no other model utterance has taken the first consulting turn.
            if (hasActualModelText) return prev
            appended = true
            return [...prev, greetingItem]
          }
          const fallbackGreeting = prev[fallbackGreetingIndex]
          if (!fallbackGreeting) return prev
          if (
            fallbackGreeting.text === greeting &&
            !injectOptions?.replaceForLocale &&
            Boolean(fallbackGreeting.groundingMetadata) === Boolean(greetingItem.groundingMetadata)
          ) {
            return prev
          }

          const keptTimestamp =
            fallbackGreeting.timestamp instanceof Date
              ? fallbackGreeting.timestamp
              : new Date(fallbackGreeting.timestamp)
          const replacement = {
            ...greetingItem,
            id: fallbackGreeting.id,
            timestamp: keptTimestamp,
          }
          greetingToPersist = replacement
          appended = true
          const next = [...prev]
          next[fallbackGreetingIndex] = replacement
          return next
        }

        if (hasActualModelText) return prev
        appended = true
        return [...prev, greetingItem]
      })
      if (appended) {
        if (!openerTimestampRef.current) {
          const kept = greetingToPersist.timestamp
          openerTimestampRef.current = kept instanceof Date ? kept : new Date(kept)
        }
        greetingToPersist = { ...greetingToPersist, timestamp: openerTimestampRef.current }
        clientSessionViewStore.upsertOptimisticTranscriptItem(greetingToPersist)
        initialGreetingSentRef.current = true
      }
    },
    [locale, setTranscript, t]
  )

  useEffect(() => {
    const payload = lastGreetingPayloadRef.current
    if (!payload || !initialGreetingSentRef.current) return
    maybeInjectPersonalizedGreeting(payload, { replaceForLocale: true })
  }, [locale, maybeInjectPersonalizedGreeting])

  const performBackgroundResearch = useCallback(
    async (email: string, name: string, companyUrl?: string) => {
      const domain = email.split('@')[1]?.toLowerCase()

      // Admin / founder bypass: never research yourself
      if (email.toLowerCase().trim() === ADMIN_CONFIG.ADMIN_EMAIL.toLowerCase()) {
        logger.debug('Admin session detected — skipping lead research.')
        const fallback = buildFallbackResearchResult(email, name, companyUrl)
        fallback.person.fullName = name || 'Farzad'
        fallback.person.company = 'Farzad Bayat Consulting'
        fallback.company.name = 'Farzad Bayat Consulting'
        fallback.company.domain = 'bayatconsulting.com'
        fallback.company.website = companyUrl || 'https://bayatconsulting.com'
        fallback.company.summary = 'AI strategy and implementation consulting'
        fallback.role = 'Founder & AI Consultant'
        fallback.confidence = 1.0
        applyServerResearchToContext(
          email,
          name,
          companyUrl,
          { research: fallback, intelligenceContext: { name: name || 'Farzad', email, company: { name: 'Farzad Bayat Consulting' }, trustedIdentity: true } },
          { autoRevealBriefing: false }
        )
        return
      }

      if (isGenericOrFreeDomain(domain)) {
        const plate = splitCompanyUrlOrName(companyUrl).statedCompany?.trim().slice(0, 120) ?? ''
        logger.debug('Free or reserved email domain, skipping company lookup.', { domain })
        const fallback = buildPlateOnlyResearchResult(email, name, plate)
        applyServerResearchToContext(
          email,
          name,
          plate || undefined,
          { research: fallback },
          { autoRevealBriefing: false }
        )
        return
      }

      logger.debug('Triggering Background Lead Research for:', {
        email,
        hasLocation: Boolean(locationData),
      })
      setIsResearching(true)
      setResearchStatus('Initializing session...')

      try {
        const allowedDomains = derivePreferredDomains(email, companyUrl)

        const response = await postJson<{
          ok: boolean
          allowResearch?: boolean
          trustedIdentity?: boolean
          research?: ResearchResult
          intelligenceContext?: any
          error?: string
        }>('/api/intelligence/init-session', {
          sessionId,
          email,
          name,
          companyUrl,
          allowResearch: sessionPermissions?.research === true,
          forceFresh: true,
          ...(allowedDomains.length > 0 ? { allowedDomains } : {}),
        })

        if (!response.ok) throw new Error(response.error || 'Research failed')
        setResearchStatus('Building intelligence context...')
        applyServerResearchToContext(email, name, companyUrl, response, {
          autoRevealBriefing: true,
        })

        // Do not inject low-value system transcript items.
        // Research status is surfaced via isResearching + context/sources UI.
      } catch (e) {
        logger.error('Background Research failed', e instanceof Error ? e : new Error(String(e)))
        researchResultRef.current = null
      } finally {
        setIsResearching(false)
        setResearchStatus('')
      }
    },
    [
      applyServerResearchToContext,
      locationData,
      postJson,
      researchResultRef,
      sessionId,
      sessionPermissions,
    ]
  )

  const performResearch = useCallback(
    async (input: string) => {
      // Determine if we should auto-research based on email domain
      const email = (userProfile?.email || '').trim()
      const domain = email.split('@')[1]?.toLowerCase() || ''
      const genericDomains = ['gmail.com', 'outlook.com', 'hotmail.com', 'yahoo.com', 'icloud.com', 'proton.me', 'protonmail.com']
      const isBusinessEmail = !genericDomains.includes(domain)
      
      const permissionGranted = sessionPermissions?.research === true
      const explicitRequest =
        /\b(research|look up|lookup|find out|background check|what did you find|tell me about|who is|enrich)\b/i.test(
          input
        )
      
      // AUTO-RESEARCH RULE (per blueprint):
      // Business email = auto-research on first message
      // Personal email = require explicit permission or request
      const shouldResearch = isBusinessEmail || permissionGranted || explicitRequest

      // Only run research if conditions met
      if (!shouldResearch) return
      if (!email) return
      if (researchResultRef.current) return // Already researched

      // Admin / founder bypass: skip API call, use local fallback
      if (email.toLowerCase().trim() === ADMIN_CONFIG.ADMIN_EMAIL.toLowerCase()) {
        logger.debug('Admin session detected — skipping performResearch API call.')
        const fallback = buildFallbackResearchResult(email, userProfile?.name, undefined)
        fallback.person.fullName = userProfile?.name || 'Farzad'
        fallback.person.company = 'Farzad Bayat Consulting'
        fallback.company.name = 'Farzad Bayat Consulting'
        fallback.company.domain = 'bayatconsulting.com'
        fallback.company.website = 'https://bayatconsulting.com'
        fallback.company.summary = 'AI strategy and implementation consulting'
        fallback.role = 'Founder & AI Consultant'
        fallback.confidence = 1.0
        applyServerResearchToContext(
          email,
          userProfile?.name,
          undefined,
          {
            research: fallback,
            intelligenceContext: {
              name: userProfile?.name || 'Farzad',
              email,
              company: { name: 'Farzad Bayat Consulting' },
              trustedIdentity: true,
            },
          },
          { autoRevealBriefing: false }
        )
        return
      }

      if (isGenericOrFreeDomain(domain)) {
        const plate = splitCompanyUrlOrName(userProfile?.company).statedCompany?.trim().slice(0, 120) ?? ''
        const fallback = buildPlateOnlyResearchResult(email, userProfile?.name, plate)
        applyServerResearchToContext(email, userProfile?.name, plate || undefined, { research: fallback }, {
          autoRevealBriefing: false,
        })
        return
      }

      logger.debug('Triggering Lead Research for:', { email, explicitRequest, permissionGranted })

      // Do not inject low-value system transcript items.
      // Research status is surfaced via isResearching + context/sources UI.

      try {
        const allowedDomains = derivePreferredDomains(email)

        const inferredName =
          userProfile?.name ||
          (intelligenceContextRef.current?.name as string | undefined) ||
          undefined

        const response = await postJson<{
          ok: boolean
          allowResearch?: boolean
          trustedIdentity?: boolean
          research?: ResearchResult
          intelligenceContext?: any
          error?: string
        }>('/api/intelligence/init-session', {
          sessionId,
          email,
          ...(inferredName ? { name: inferredName } : {}),
          allowResearch: sessionPermissions?.research === true,
          forceFresh: true,
          ...(allowedDomains.length > 0 ? { allowedDomains } : {}),
        })

        if (!response.ok) throw new Error(response.error || 'Research failed')
        applyServerResearchToContext(email, inferredName, undefined, response, {
          autoRevealBriefing: true,
        })

        // Do not inject low-value system transcript items.
        // Research status is surfaced via isResearching + context/sources UI.
      } catch (e: any) {
        logger.error('Research failed', e instanceof Error ? e : new Error(String(e)))
        showToast(t('toast.researchTimeout'), 'info')
      }
    },
    [
      applyServerResearchToContext,
      intelligenceContextRef,
      postJson,
      researchResultRef,
      sessionId,
      sessionPermissions,
      setVisualState,
      showToast,
      userProfile,
    ]
  )

  const handleTermsComplete = async (
    name: string,
    email: string,
    companyUrl?: string,
    permissions?: CanonicalSessionPermissionsV1
  ): Promise<boolean> => {
    // One opener language, chosen before the greeting is written. The browser
    // language applies only when the visitor has not already chosen. The email
    // TLD does not flip an English plate to Norwegian.
    const opener = openerLocaleForPlate(
      email,
      typeof navigator !== 'undefined' ? navigator.language : null
    )
    if (
      (opener === 'nb' || opener === 'nn') &&
      !isUiLocaleExplicit(clientSessionViewStore.getSnapshot())
    ) {
      clientSessionViewStore.setUiLocale('nb', { explicit: true })
    }
    const sessionLocale = coerceUiLocale(clientSessionViewStore.getSnapshot().uiLocale) ?? 'en'
    const savedProfileRaw =
      typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('fbc_user_profile') : null
    let previousEmail: string | undefined
    if (savedProfileRaw) {
      try {
        previousEmail = (JSON.parse(savedProfileRaw) as { email?: string }).email
      } catch {
        previousEmail = undefined
      }
    }

    const emailMismatch =
      shouldMintFreshSessionForIntake({ name, email }) ||
      shouldResetPersonaForEmail(previousEmail, email)

    // Homepage writes fbc_session_id. /chat may 404 that id and retire it.
    // persistConsent waits for that lookup once, then reads the id the browser
    // holds. The retry uses the same wait. A projection for any other session
    // is still rejected.
    let activeSessionId = clientSessionViewStore.getSnapshot().sessionId?.trim() || sessionId
    if (emailMismatch) {
      activeSessionId = mintFreshClientSessionForNewVisitor()
      initialGreetingSentRef.current = false
      openerTimestampRef.current = null
      lastGreetingPayloadRef.current = null
      researchResultRef.current = null
      intelligenceContextRef.current = {}
    }

    const profile: UserProfile = {
      name,
      email,
      ...(companyUrl?.trim() ? { company: companyUrl.trim() } : {}),
      source: 'intake',
    }
    logger.debug('[App] handleTermsComplete started', { name, email, companyUrl, permissions })
    setUserProfile(profile)
    sessionStorage.setItem('fbc_user_profile', JSON.stringify(profile))

    if (!permissions?.research) {
      researchResultRef.current = null
    }
    let locationGranted = false
    let resolvedLocation: typeof locationData = null

    if (permissions) {
      logger.debug('[App] Applying user permissions:', permissions)
      setUiSessionPermissions(permissions)

      if (permissions.webcam) {
        setIsWebcamActive(true)
      }

      if (permissions.location) {
        try {
          logger.debug('[App] Requesting location access...')
          const location = await clientSessionViewStore.acquireBrowserLocation()

          if (location) {
            logger.debug('[App] Location access granted:', location)
            setLocationData(location)
            locationGranted = true
            // Store for immediate use in init-session (avoids stale closure)
            resolvedLocation = location
          }
        } catch (err) {
          logger.warn('[App] Location access failed', { error: String(err) })
        }
      }
    }

    // Persist consent (+ visitor identity) BEFORE unlocking chat. Chat reads identity
    // only from the server projection — client greetings from form name are not enough.
    const persistConsent = async () => {
      await waitForClientSessionLookup()
      const liveSessionId = clientSessionViewStore.getSnapshot().sessionId?.trim()
      if (liveSessionId) activeSessionId = liveSessionId
      return postJson<{
        ok: boolean
        error?: string
        projectionEnvelope?: unknown
      }>('/api/consent', {
        sessionId: activeSessionId,
        email,
        name,
        companyUrl,
        allowResearch: Boolean(permissions?.research),
        uiLocale: sessionLocale,
        ...(permissions ? { permissions } : {}),
        ...(permissions?.location ? { locationGranted } : {}),
      })
    }

    const visitorLanded = (response: {
      ok: boolean
      projectionEnvelope?: unknown
    }): boolean => {
      if (!response.ok) return false
      acceptEmbeddedSessionProjection(response, 'consent:handleTermsComplete')
      const visitor = clientSessionViewStore.getServerProjectionSnapshot()?.projection?.visitor
      const landedName = visitor?.name?.trim()
      const landedEmail = visitor?.email?.trim()
      return Boolean(
        landedName && landedEmail && visitorEmailsMatch(landedEmail, email),
      )
    }

    let consentOk = false
    try {
      const consentResponse = await persistConsent()
      if (!consentResponse.ok) {
        throw new Error(consentResponse.error || 'Consent persistence failed')
      }
      consentOk = visitorLanded(consentResponse)
      if (!consentOk) {
        throw new Error('Consent succeeded but server visitor identity is empty')
      }
    } catch (err) {
      logger.warn('[LeadResearch] Consent persist failed — retrying once', {
        error: err instanceof Error ? err.message : String(err),
      })
      try {
        const retry = await persistConsent()
        if (!retry.ok) throw new Error(retry.error || 'Consent persistence failed')
        consentOk = visitorLanded(retry)
        if (!consentOk) {
          throw new Error('Consent succeeded but server visitor identity is empty')
        }
      } catch (retryErr) {
        logger.error(
          '[LeadResearch] Consent persist failed after retry — chat identity may be empty',
          retryErr instanceof Error ? retryErr : new Error(String(retryErr)),
        )
        showToast(t('toast.consentPersistFailed'), 'error')
      }
    }

    // Consent must land before chat unlocks — concierge form is the only intake surface.
    if (!consentOk) {
      return false
    }

    setView('chat')
    setChatVisible(true)

    // Immediate first-contact: presence if no company; labeled guess if
    // intake/email-domain/consent already named one. Never the hedge.
    const snapshotIntel = clientSessionViewStore.getSnapshot().intelligenceContext
    maybeInjectPersonalizedGreeting({
      email,
      name,
      ...(companyUrl ? { companyUrl } : {}),
      ...(snapshotIntel ? { intelligenceContext: snapshotIntel } : {}),
    })

    // C4: Research then UPGRADE the presence bubble to a labeled guess.
    const initPromise = (async () => {
      setIsResearching(true)
      setPreflightFailed(false)
      setPreflightTimedOut(false)
      if (preflightTimerRef.current) {
        clearTimeout(preflightTimerRef.current)
        preflightTimerRef.current = null
      }
      if (presenceTimerRef.current) {
        clearTimeout(presenceTimerRef.current)
        presenceTimerRef.current = null
      }
      if (permissions?.research) setResearchStatus(t('concierge.analyzingCompanyProfile'))

      const allowedDomains = derivePreferredDomains(email, companyUrl)

      // ~5s without research: keep presence, unlock composer, upgrade when research lands.
      presenceTimerRef.current = setTimeout(() => {
        setIsResearching(false)
        setResearchStatus('')
        maybeInjectPersonalizedGreeting({ email, name, ...(companyUrl ? { companyUrl } : {}) })
      }, GREETING_PRESENCE_MS)

      // Hard UI timeout: never let the user stay stuck on the preflight panel
      preflightTimerRef.current = setTimeout(() => {
        setPreflightTimedOut(true)
        setPreflightFailed(true)
        setIsResearching(false)
        setResearchStatus('')
        maybeInjectPersonalizedGreeting({ email, name, ...(companyUrl ? { companyUrl } : {}) })
        showToast(t('toast.preflightTimeout'), 'error')
      }, PREFLIGHT_UI_TIMEOUT_MS)

      try {
        const autoResearch = Boolean(permissions?.research)

        const locationForVerify = resolvedLocation || locationData
        const cityContext = locationForVerify
          ? `Lat: ${locationForVerify.latitude}, Lng: ${locationForVerify.longitude}`
          : 'Unknown'

        const verifyPromise = postJson<VerificationResult>('/api/verify-identity', {
          name,
          email,
          ...(companyUrl ? { company: companyUrl } : {}),
          location: { city: cityContext },
        }).catch(err => {
          logger.warn('[LeadResearch] Identity verification failed (non-fatal)', {
            error: err instanceof Error ? err.message : String(err),
          })
          return undefined
        })

        const [response, verification] = await Promise.all([
          runInitSessionOnce(activeSessionId, () =>
            postJson<{
              ok: boolean
              allowResearch?: boolean
              trustedIdentity?: boolean
              research?: ResearchResult
              intelligenceContext?: any
              error?: string
            }>('/api/intelligence/init-session', {
              sessionId: activeSessionId,
              email,
              name,
              companyUrl,
              allowResearch: autoResearch,
              permissions: permissions ?? {
                research: autoResearch,
                location: false,
                voice: false,
                webcam: false,
                screen: false,
                files: false,
              },
              uiLocale: sessionLocale,
              ...(allowedDomains.length > 0 ? { allowedDomains } : {}),
              forceFresh: emailMismatch,
              fastMode: true,
              openingBudget: true,
            })
          ),
          verifyPromise,
        ])

        if (!response.ok) throw new Error(response.error || 'Init session failed')
        setResearchStatus('Building intelligence context...')

        const mergedPayload = {
          ...response,
          intelligenceContext: {
            ...(response.intelligenceContext || {}),
            ...(verification ? { verification } : {}),
          },
        }

        applyServerResearchToContext(email, name, companyUrl, mergedPayload, {
          autoRevealBriefing: true,
        })
        // Upgrade presence → labeled guess from the init payload (not a stale store ref).
        maybeInjectPersonalizedGreeting({
          email,
          name,
          ...(companyUrl ? { companyUrl } : {}),
          ...(mergedPayload.research
            ? { research: mergedPayload.research }
            : researchResultRef.current
              ? { research: researchResultRef.current }
              : {}),
          ...(mergedPayload.intelligenceContext || intelligenceContextRef.current
            ? {
                intelligenceContext:
                  mergedPayload.intelligenceContext ?? intelligenceContextRef.current,
              }
            : {}),
          ...(mergedPayload.trustedIdentity !== undefined
            ? { trustedIdentity: mergedPayload.trustedIdentity }
            : {}),
        })
        return mergedPayload
      } catch (e: any) {
        if (e.name === 'AbortError') {
          showToast(t('toast.researchTimeout'), 'info')
        }
        // Presence only — never the hedge survey. Upgrade later if research arrives.
        maybeInjectPersonalizedGreeting({ email, name, ...(companyUrl ? { companyUrl } : {}) })
        setPreflightFailed(true)
        throw e
      } finally {
        if (preflightTimerRef.current) {
          clearTimeout(preflightTimerRef.current)
          preflightTimerRef.current = null
        }
        if (presenceTimerRef.current) {
          clearTimeout(presenceTimerRef.current)
          presenceTimerRef.current = null
        }
        setIsResearching(false)
        setResearchStatus('')
      }
    })()

    const shouldAutoStartVoice = Boolean(permissions?.voice) || pendingVoiceAutostartRef.current
    pendingVoiceAutostartRef.current = false

    // First word does not wait on research. Opening pass is fastMode.
    // The JSON dossier runs after that and only upgrades the line.
    if (shouldAutoStartVoice) {
      logger.debug('[App] Voice connects now; research continues in the background')
      void (async () => {
        const canConnectVoice = await waitForChatVoiceAutostart()
        if (!canConnectVoice) {
          logger.debug('[App] Skipping voice auto-connect because chat view is not active')
          return
        }
        onVoiceConnect()
      })()
    }

    const runDeepDossier = () => {
      if (!permissions?.research) return
      const deepDomains = derivePreferredDomains(email, companyUrl)
      void runInitSessionOnce(activeSessionId, () =>
          postJson<{
            ok: boolean
            trustedIdentity?: boolean
            research?: ResearchResult
            intelligenceContext?: any
            error?: string
          }>('/api/intelligence/init-session', {
            sessionId: activeSessionId,
            email,
            name,
            companyUrl,
            allowResearch: true,
            permissions: permissions ?? {
              research: true,
              location: false,
              voice: false,
              webcam: false,
              screen: false,
              files: false,
            },
            uiLocale: sessionLocale,
            ...(deepDomains.length > 0 ? { allowedDomains: deepDomains } : {}),
            forceFresh: emailMismatch,
            fastMode: false,
            openingBudget: false,
          }),
        )
          .then(deep => {
            if (!deep?.ok) return
            applyServerResearchToContext(email, name, companyUrl, deep, {
              autoRevealBriefing: true,
            })
            maybeInjectPersonalizedGreeting({
              email,
              name,
              ...(companyUrl ? { companyUrl } : {}),
              ...(deep.research ? { research: deep.research } : {}),
              ...(deep.intelligenceContext
                ? { intelligenceContext: deep.intelligenceContext }
                : {}),
              ...(deep.trustedIdentity !== undefined
                ? { trustedIdentity: deep.trustedIdentity }
                : {}),
            })
          })
          .catch(error => {
            logger.warn('[LeadResearch] Deep dossier failed after the opening pass', {
              error: error instanceof Error ? error.message : String(error),
            })
          })
    }

    void initPromise.then(() => runDeepDossier()).catch(() => runDeepDossier())
    return true
  }

  const handleStartChatRequest = (startVoice: boolean = false) => {
    const allowImmediateVoiceAutostart = startVoice
    pendingVoiceAutostartRef.current = startVoice

    // Always transition to chat view first
    setView('chat')
    setChatVisible(true)

    if (userProfile) {
      // Existing user - connect voice if requested
      if (allowImmediateVoiceAutostart) {
        setTimeout(() => {
          void (async () => {
            const canConnectVoice = await waitForChatVoiceAutostart()
            if (!canConnectVoice) {
              logger.debug(
                '[App] Skipping landing-page voice auto-connect because chat view is not active'
              )
              return
            }
            onVoiceConnect()
          })()
        }, 100)
      } else if (startVoice) {
        logger.debug('[App] Ignoring voice auto-start request because flow originated outside chat')
      }
    } else {
      // New user intake is handled inline by FutureChatPage concierge.
    }
  }

  return {
    userProfile,
    setUserProfile,
    locationData,
    isResearching,
    researchStatus,
    setIsResearching, // Expose setter for Glass Box UI (reasoning events)
    preflightFailed,
    preflightTimedOut,
    sessionPermissions,
    researchResultRef,
    intelligenceContextRef,
    performBackgroundResearch,
    performResearch,
    handleTermsComplete,
    handleStartChatRequest,
  }
}
